import { Box } from "@chakra-ui/layout";
import { Flex } from "@chakra-ui/react";
import useConfirmarAnuncio from "features/anuncio/hooks/useConfirmarAnuncio";
import { useListarAnuncios } from "features/anuncio/hooks/useListarAnuncios";
import useRejeitarAnuncio from "features/anuncio/hooks/useRejeitarAnuncio";
import { AnuncioStatusEnum } from "features/anuncio/typings";
import colors from "resources/theme/colors";
import history from "infra/helpers/history";
import { useState } from "react";
import SelectableButton from "components/Button/SelectableButton";
import ModalRejeitarMotivo from "components/ModalRejeitarMotivo/ModalRejeitarMotivo";
import NewTable from "components/NewTable";
import HeaderTable from "components/Page/HeaderTable";
import PageHeader from "components/Page/PageHeader";
import useFiltroAnuncios from "./hooks/useFiltroAnuncios";
import useAnunciosColumns from "./hooks/useListaAnunciosColumns";
import { showSuccess, showErrorAxios } from "infra/helpers/alerta";
import { trackEvent } from "infra/helpers/event";
import { AnuncioDto } from "features/anuncio/typings";
import { showConfirm } from "infra/helpers/alerta";
import { PermissaoEnum } from "resources/enums/permissao";
import useAnalisarAnuncio from "core/features/anuncio/hooks/useAnalisarAnuncio";
import { useLoading } from "components/GlobalLoading";

function ListaAnuncios() {
  const { filtro, onChangeFiltro } = useFiltroAnuncios();
  const { data, isLoading, refetch } = useListarAnuncios(filtro);
  const columns = useAnunciosColumns();
  const onUnselect = () => onChangeFiltro({ statusId: undefined });

  const { mutate: confirmarAnuncio, isLoading: isLoadingConfirmar } =
    useConfirmarAnuncio();
  const { mutate: rejeitarAnuncio, isLoading: isLoadingRejeitar } =
    useRejeitarAnuncio();
  const { mutate: analisarAnuncio, isLoading: isLoadingAnalisarAnuncio } =
    useAnalisarAnuncio();

  const [idAnuncioRejeitar, setIdAnuncioRejeitar] = useState<
    string | undefined
  >();

  const onAnalisar = (anuncio: AnuncioDto) => {
    const navigate = () =>
      history.push(`/administracao/anuncios/${anuncio.id}`);

    if (anuncio.status.id === AnuncioStatusEnum.Pendente) {
      showConfirm(
        "Ao acessar esse anúncio você se tornará o responsável pela análise do mesmo. Deseja continuar?"
      ).then(({ isConfirmed }) => {
        if (isConfirmed) {
          analisarAnuncio(anuncio.id, { onSuccess: navigate });
        }
      });
    } else if (anuncio.status.id !== AnuncioStatusEnum.EmAnalise) {
      analisarAnuncio(anuncio.id, { onSuccess: navigate });
    } else {
      navigate();
    }
  };

  const _isLoadingSave =
    isLoadingConfirmar || isLoadingRejeitar || isLoadingAnalisarAnuncio;

  useLoading(_isLoadingSave);

  return (
    <>
      <PageHeader title="Gestão de Anúncios" mb="24px" />

      <Box>
        <NewTable<AnuncioDto>
          title={() => (
            <HeaderTable
              value={filtro.search || ""}
              onSearch={(v) => onChangeFiltro({ search: v })}
            >
              <Flex flex={1} justifyContent="flex-end" gridGap="16px">
                <SelectableButton
                  selected={filtro.statusId === AnuncioStatusEnum.Pendente}
                  onClick={() => {
                    onChangeFiltro({ statusId: AnuncioStatusEnum.Pendente });
                  }}
                  onUnselect={onUnselect}
                  rounded="full"
                  selectedColor={colors.yellow}
                  fontSize="14px"
                >
                  Pendente
                </SelectableButton>
                <SelectableButton
                  selected={filtro.statusId === AnuncioStatusEnum.EmAnalise}
                  onClick={() => {
                    onChangeFiltro({ statusId: AnuncioStatusEnum.EmAnalise });
                  }}
                  onUnselect={onUnselect}
                  rounded="full"
                  selectedColor={colors.yellow}
                  fontSize="14px"
                >
                  Em análise
                </SelectableButton>
                <SelectableButton
                  selected={filtro.statusId === AnuncioStatusEnum.Aprovado}
                  onClick={() => {
                    onChangeFiltro({ statusId: AnuncioStatusEnum.Aprovado });
                  }}
                  onUnselect={onUnselect}
                  rounded="full"
                  selectedColor={colors.green500}
                  fontSize="14px"
                >
                  Aprovado
                </SelectableButton>
                <SelectableButton
                  selected={filtro.statusId === AnuncioStatusEnum.Rejeitado}
                  onClick={() => {
                    onChangeFiltro({ statusId: AnuncioStatusEnum.Rejeitado });
                  }}
                  onUnselect={onUnselect}
                  rounded="full"
                  selectedColor={colors.red}
                  fontSize="14px"
                >
                  Rejeitado
                </SelectableButton>
              </Flex>
            </HeaderTable>
          )}
          columns={columns}
          dataSource={data?.items || []}
          loading={isLoading}
          onRow={(record) => ({
            onClick: () => onAnalisar(record),
            style: { cursor: "pointer" },
          })}
          actions={[
            {
              title: "Aprovar",
              permission:
                PermissaoEnum.Backoffice.Administrativo.Anuncio.Aprovar,
              onClick: (v: AnuncioDto) => {
                trackEvent("Anuncios_Aprovar");
                showConfirm("Tem certeza que deseja aprovar esse anúncio?", {
                  confirmButtonText: "Aprovar",
                }).then(({ isConfirmed }) => {
                  if (isConfirmed) {
                    trackEvent("Anuncios_Salvar_Aprovar");
                    confirmarAnuncio(v.id, {
                      onError: (e) => showErrorAxios(e),
                      onSuccess: () =>
                        showSuccess("Anuncio aprovado com sucesso").then(() => {
                          refetch();
                        }),
                    });
                  }
                });
              },
            },
            {
              title: "Rejeitar",
              permission:
                PermissaoEnum.Backoffice.Administrativo.Anuncio.Rejeitar,
              onClick: (v: AnuncioDto) => {
                trackEvent("Anuncios_Recusar");
                setIdAnuncioRejeitar(v.id);
              },
            },
          ]}
        />
      </Box>

      <ModalRejeitarMotivo
        isLoadingSave={_isLoadingSave}
        visible={!!idAnuncioRejeitar}
        onClose={() => setIdAnuncioRejeitar(undefined)}
        onSubmit={(motivo) => {
          trackEvent("Anuncios_Salvar_Recusar");
          rejeitarAnuncio(
            {
              id: idAnuncioRejeitar!,
              motivo,
            },
            {
              onError: (e) => showErrorAxios(e),
              onSuccess: () =>
                showSuccess("Anuncio rejeitado com sucesso").then(() => {
                  setIdAnuncioRejeitar(undefined);
                  refetch();
                }),
            }
          );
        }}
      />
    </>
  );
}

export default ListaAnuncios;
